import { tr } from "@/warRoom/lib/i18n";

/**
 * 把畫布畫成一張 PNG。
 *
 * 不引第三方的 DOM→圖片套件：圖表本身是 G2 畫在 <canvas> 上的，
 * 可以直接 drawImage 過來（像素等於螢幕上看到的）；剩下的卡片外框、標題、
 * 表格文字用 computed style + 元素座標重畫一遍。
 * 所以這裡只處理三件事：背景／框線、<canvas> 與 <img>、文字節點。
 *
 * 匯出的是「畫布上排好的版面」，不是螢幕截圖 —— 捲動位置、選取外框、
 * 工具鈕（data-export="skip"）都不會進去。
 */

type Options = {
  /** 底色：用當前主題的版面色，不要透明背景（貼到文件裡會變黑底） */
  background: string;
  /** 內容四周留白 */
  padding?: number;
  /** 解析度倍率 */
  scale?: number;
};

const isTransparent = (color: string) => {
  if (!color || color === "transparent") return true;
  const m = color.match(/rgba?\(([^)]+)\)/);
  if (!m) return false;
  const parts = m[1].split(",").map((v) => parseFloat(v));
  return parts.length >= 4 && parts[3] === 0;
};

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

type Origin = { left: number; top: number };

/** 文字節點：用 Range 取每一行的實際位置，才不會把換行後的字疊在一起 */
function paintText(ctx: CanvasRenderingContext2D, node: Text, origin: Origin) {
  const raw = node.nodeValue;
  const parent = node.parentElement;
  if (!raw || !raw.trim() || !parent) return;

  const style = getComputedStyle(parent);
  if (isTransparent(style.color)) return;
  ctx.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
  ctx.fillStyle = style.color;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";

  const range = document.createRange();
  range.selectNodeContents(node);
  const lines = [...range.getClientRects()];

  if (lines.length === 1) {
    const r = lines[0];
    ctx.fillText(
      raw,
      r.left - origin.left,
      (r.top + r.bottom) / 2 - origin.top,
    );
    return;
  }

  // 折了行：逐字問位置，把同一行（top 相同）的字收成一段再畫
  let start = 0;
  let lineTop: number | null = null;
  let lineLeft = 0;
  const flush = (end: number) => {
    if (lineTop === null || end <= start) return;
    range.setStart(node, start);
    range.setEnd(node, end);
    const r = range.getBoundingClientRect();
    ctx.fillText(
      raw.slice(start, end),
      lineLeft - origin.left,
      (r.top + r.bottom) / 2 - origin.top,
    );
  };
  for (let i = 0; i < raw.length; i += 1) {
    range.setStart(node, i);
    range.setEnd(node, i + 1);
    const r = range.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) continue;
    if (lineTop === null) {
      lineTop = r.top;
      lineLeft = r.left;
      start = i;
    } else if (Math.abs(r.top - lineTop) > 1) {
      flush(i);
      lineTop = r.top;
      lineLeft = r.left;
      start = i;
    }
  }
  flush(raw.length);
}

function paintNode(ctx: CanvasRenderingContext2D, node: Node, origin: Origin) {
  if (node.nodeType === Node.TEXT_NODE) {
    paintText(ctx, node as Text, origin);
    return;
  }
  if (!(node instanceof HTMLElement)) return;

  // 工具鈕、縮放把手這種「操作用」的東西不進圖片
  if (node.dataset.export === "skip") return;

  const style = getComputedStyle(node);
  // opacity 讀不到值時當作看得見 —— 讀不到不等於透明
  const opacity = parseFloat(style.opacity);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    opacity === 0
  )
    return;

  const rect = node.getBoundingClientRect();
  const x = rect.left - origin.left;
  const y = rect.top - origin.top;
  const radius = parseFloat(style.borderTopLeftRadius) || 0;

  if (rect.width > 0 && rect.height > 0) {
    if (!isTransparent(style.backgroundColor)) {
      roundRectPath(ctx, x, y, rect.width, rect.height, radius);
      ctx.fillStyle = style.backgroundColor;
      ctx.fill();
    }

    // 四個邊分開畫：表格的列只有 border-bottom，卡片是四邊都有
    const edges = [
      {
        w: parseFloat(style.borderTopWidth),
        c: style.borderTopColor,
        r: [x, y, rect.width, parseFloat(style.borderTopWidth)],
      },
      {
        w: parseFloat(style.borderBottomWidth),
        c: style.borderBottomColor,
        r: [
          x,
          y + rect.height - parseFloat(style.borderBottomWidth),
          rect.width,
          parseFloat(style.borderBottomWidth),
        ],
      },
      {
        w: parseFloat(style.borderLeftWidth),
        c: style.borderLeftColor,
        r: [x, y, parseFloat(style.borderLeftWidth), rect.height],
      },
      {
        w: parseFloat(style.borderRightWidth),
        c: style.borderRightColor,
        r: [
          x + rect.width - parseFloat(style.borderRightWidth),
          y,
          parseFloat(style.borderRightWidth),
          rect.height,
        ],
      },
    ];
    for (const e of edges) {
      if (!e.w || isTransparent(e.c)) continue;
      ctx.fillStyle = e.c;
      ctx.fillRect(e.r[0], e.r[1], e.r[2], e.r[3]);
    }
  }

  if (node instanceof HTMLCanvasElement) {
    if (rect.width > 0 && rect.height > 0) {
      try {
        ctx.drawImage(node, x, y, rect.width, rect.height);
      } catch {
        // 跨來源的 canvas 會 taint，畫不了就留白，不要讓整張圖失敗
      }
    }
    return;
  }
  if (node instanceof HTMLImageElement) {
    if (node.complete && rect.width > 0) {
      try {
        ctx.drawImage(node, x, y, rect.width, rect.height);
      } catch {
        /* 同上 */
      }
    }
    return;
  }

  // 溢出被裁掉的（省略號的標題、捲動中的表格）要跟著裁，不然文字會畫到卡片外面
  const clipped =
    style.overflow !== "visible" && rect.width > 0 && rect.height > 0;
  if (clipped) {
    ctx.save();
    roundRectPath(ctx, x, y, rect.width, rect.height, radius);
    ctx.clip();
  }
  for (const child of [...node.childNodes]) paintNode(ctx, child, origin);
  if (clipped) ctx.restore();
}

/** 只畫卡片本身 —— 畫布的點陣底紋不進圖片，輸出的是乾淨底色 */
export async function surfaceToBlob(
  surface: HTMLElement,
  opts: Options,
): Promise<Blob> {
  const padding = opts.padding ?? 24;
  const scale = opts.scale ?? 2;

  const cards = [...surface.querySelectorAll<HTMLElement>("[data-widget]")];
  if (cards.length === 0) throw new Error(tr("warRoom.export.noWidgets"));

  const rects = cards.map((c) => c.getBoundingClientRect());
  const left = Math.min(...rects.map((r) => r.left)) - padding;
  const top = Math.min(...rects.map((r) => r.top)) - padding;
  const width = Math.max(...rects.map((r) => r.right)) + padding - left;
  const height = Math.max(...rects.map((r) => r.bottom)) + padding - top;

  const out = document.createElement("canvas");
  out.width = Math.ceil(width * scale);
  out.height = Math.ceil(height * scale);
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error(tr("warRoom.export.unsupported"));

  ctx.scale(scale, scale);
  ctx.fillStyle = opts.background;
  ctx.fillRect(0, 0, width, height);

  // 選取外框是「操作狀態」，不該印在圖片上
  surface.classList.add("is-exporting");
  try {
    for (const card of cards) paintNode(ctx, card, { left, top });
  } finally {
    surface.classList.remove("is-exporting");
  }

  return await new Promise<Blob>((resolve, reject) => {
    out.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error(tr("warRoom.export.failed"))),
      "image/png",
    );
  });
}

export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // 讓瀏覽器有時間把檔案接走再收掉
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
